import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { HiMagnifyingGlassPlus, HiMagnifyingGlassMinus } from 'react-icons/hi2';
import Button from './ui/Button';
import { formatDateShort, getWeekStartDate } from '../utils/helpers.ts';
import { getColorForCourse, brightenHexColor, mapTailwindBgToColor } from '../utils/colorUtils';

const RoomsOccupancyView = ({
    rooms = [],
    scheduleEntries = [],
    selectedPeriod = null,
    activeDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
    holidays = new Map(), // Map<dateString, Holiday>
    onRoomClick = null,
    onTimeSlotClick = null
}) => {
    // Zoom levels: minutes per pixel
    const zoomLevels = [
        { label: '4h', minutesPerPixel: 4 }, // Very zoomed in - 4 minutes per pixel
        { label: '8h', minutesPerPixel: 8 }, // Zoomed in - 8 minutes per pixel  
        { label: '15m', minutesPerPixel: 15 }, // Medium - 15 minutes per pixel
        { label: '30m', minutesPerPixel: 30 }, // Zoomed out - 30 minutes per pixel
        // { label: '1h', minutesPerPixel: 60 }, // Very zoomed out - 1 hour per pixel
    ];

    const [zoomLevel, setZoomLevel] = useState(2); // Start at medium zoom
    // Virtualization range for visible rooms
    const [visibleRange, setVisibleRange] = useState({ start: 0, end: Math.min(rooms.length, 30) });

    const containerRef = useRef(null);
    const timeHeaderRef = useRef(null);
    const roomHeaderRef = useRef(null);
    const gridRef = useRef(null);

    // Constants
    const ROOM_ROW_HEIGHT = 60; // Height of each room row in pixels
    const TIME_HEADER_HEIGHT = 80; // Height of time header
    const ROOM_HEADER_WIDTH = 200; // Width of room names column
    const DAY_START_HOUR = 7; // Start at 7 AM
    const DAY_END_HOUR = 22; // End at 10 PM
    const TOTAL_MINUTES_PER_DAY = (DAY_END_HOUR - DAY_START_HOUR) * 60; // 15 hours = 900 minutes

    // Overscan rows above/below viewport for smoother scrolling
    const OVERSCAN_ROWS = 5;

    // Tooltip state for schedule items (custom tooltip with delay)
    const [tooltipState, setTooltipState] = useState({ visible: false, key: null });
    const tooltipTimerRef = useRef(null);

    // Helper: darken a hex color by mixing with black
    const darkenHexColor = useCallback((hex, amount = 0.2) => {
        if (!hex) return '#000000';
        const clean = hex.replace('#', '');
        const r = parseInt(clean.substr(0, 2), 16);
        const g = parseInt(clean.substr(2, 2), 16);
        const b = parseInt(clean.substr(4, 2), 16);
        const toHex = (n) => {
            const h = Math.max(0, Math.min(255, n)).toString(16);
            return h.length === 1 ? '0' + h : h;
        };
        const dr = Math.round(r * (1 - amount));
        const dg = Math.round(g * (1 - amount));
        const db = Math.round(b * (1 - amount));
        return `#${toHex(dr)}${toHex(dg)}${toHex(db)}`;
    }, []);

    const handleItemMouseEnter = useCallback((itemKey) => {
        if (tooltipTimerRef.current) {
            clearTimeout(tooltipTimerRef.current);
        }
        tooltipTimerRef.current = setTimeout(() => {
            setTooltipState({ visible: true, key: itemKey });
        }, 100);
    }, []);

    const handleItemMouseLeave = useCallback(() => {
        if (tooltipTimerRef.current) {
            clearTimeout(tooltipTimerRef.current);
            tooltipTimerRef.current = null;
        }
        setTooltipState({ visible: false, key: null });
    }, []);

    useEffect(() => {
        return () => {
            if (tooltipTimerRef.current) {
                clearTimeout(tooltipTimerRef.current);
            }
        };
    }, []);

    // Calculate period date range
    const periodDateRange = useMemo(() => {
        if (!selectedPeriod) return { startDate: new Date(), endDate: new Date(), days: [] };

        // Get the period's date range
        const currentYear = new Date().getFullYear();
        const startWeek = selectedPeriod.start_week;
        const endWeek = selectedPeriod.end_week;

        // Handle cross-year periods
        const startYear = selectedPeriod.year || currentYear;
        const endYear = endWeek < startWeek ? startYear + 1 : startYear;

        const startDate = getWeekStartDate(new Date(startYear, 0, 1 + (startWeek - 1) * 7));
        const endDate = getWeekStartDate(new Date(endYear, 0, 1 + (endWeek - 1) * 7));
        endDate.setDate(endDate.getDate() + 6); // End of week

        // Generate all days in the period that match activeDays
        const days = [];
        const current = new Date(startDate);
        while (current <= endDate) {
            const dayName = current.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
            if (activeDays.includes(dayName)) {
                days.push(new Date(current));
            }
            current.setDate(current.getDate() + 1);
        }

        return { startDate, endDate, days };
    }, [selectedPeriod, activeDays]);

    // Calculate grid dimensions
    const currentZoom = zoomLevels[zoomLevel];
    const dayWidth = useMemo(() => TOTAL_MINUTES_PER_DAY / currentZoom.minutesPerPixel, [currentZoom.minutesPerPixel]);
    const totalWidth = periodDateRange.days.length * dayWidth;
    const totalHeight = rooms.length * ROOM_ROW_HEIGHT;

    // Zoom controls
    const handleZoomIn = useCallback(() => {
        setZoomLevel(prev => Math.max(0, prev - 1));
    }, []);

    const handleZoomOut = useCallback(() => {
        setZoomLevel(prev => Math.min(zoomLevels.length - 1, prev + 1));
    }, []);

    // Touch/gesture handling for pinch-to-zoom
    const [lastTouchDistance, setLastTouchDistance] = useState(null);

    const handleTouchStart = useCallback((e) => {
        if (e.touches.length === 2) {
            const distance = Math.hypot(
                e.touches[0].clientX - e.touches[1].clientX,
                e.touches[0].clientY - e.touches[1].clientY
            );
            setLastTouchDistance(distance);
        }
    }, []);

    const handleTouchMove = useCallback((e) => {
        if (e.touches.length === 2 && lastTouchDistance) {
            e.preventDefault();
            const distance = Math.hypot(
                e.touches[0].clientX - e.touches[1].clientX,
                e.touches[0].clientY - e.touches[1].clientY
            );

            const ratio = distance / lastTouchDistance;
            if (ratio > 1.1) {
                handleZoomIn();
                setLastTouchDistance(distance);
            } else if (ratio < 0.9) {
                handleZoomOut();
                setLastTouchDistance(distance);
            }
        }
    }, [lastTouchDistance, handleZoomIn, handleZoomOut]);

    const handleTouchEnd = useCallback(() => {
        setLastTouchDistance(null);
    }, []);

    // Compute visible room range (virtualization) using requestAnimationFrame to throttle updates
    const rafIdRef = useRef(null);
    const lastComputedRef = useRef({ start: 0, end: 0 });

    const computeVisibleRange = useCallback((containerHeight, scrollTop) => {
        const visibleCount = Math.ceil(containerHeight / ROOM_ROW_HEIGHT);
        const startIndex = Math.max(0, Math.floor(scrollTop / ROOM_ROW_HEIGHT) - OVERSCAN_ROWS);
        const endIndex = Math.min(rooms.length, startIndex + visibleCount + OVERSCAN_ROWS * 2);

        if (lastComputedRef.current.start !== startIndex || lastComputedRef.current.end !== endIndex) {
            lastComputedRef.current = { start: startIndex, end: endIndex };
            setVisibleRange({ start: startIndex, end: endIndex });
        }
    }, [rooms.length]);

    // Scroll synchronization + virtualization update
    const handleGridScroll = useCallback((e) => {
        const { scrollLeft, scrollTop, clientHeight } = e.target;

        // Sync horizontal scroll with time header
        if (timeHeaderRef.current && timeHeaderRef.current.scrollLeft !== scrollLeft) {
            timeHeaderRef.current.scrollLeft = scrollLeft;
        }

        // Sync vertical scroll with room header
        if (roomHeaderRef.current && roomHeaderRef.current.scrollTop !== scrollTop) {
            roomHeaderRef.current.scrollTop = scrollTop;
        }

        // Throttled visible range calculation
        if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = requestAnimationFrame(() => computeVisibleRange(clientHeight, scrollTop));
    }, [computeVisibleRange]);

    // Handle room header scroll (vertical only)
    const handleRoomHeaderScroll = useCallback((e) => {
        const { scrollTop } = e.target;

        // Sync vertical scroll with grid
        if (gridRef.current && gridRef.current.scrollTop !== scrollTop) {
            gridRef.current.scrollTop = scrollTop;
        }
    }, []);

    // Initialize visible range and react to container resize
    useEffect(() => {
        const gridEl = gridRef.current;
        if (!gridEl) return;

        computeVisibleRange(gridEl.clientHeight, gridEl.scrollTop || 0);

        const ro = new ResizeObserver(() => {
            computeVisibleRange(gridEl.clientHeight, gridEl.scrollTop || 0);
        });
        ro.observe(gridEl);
        return () => {
            ro.disconnect();
            if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
        };
    }, [computeVisibleRange]);

    // Precompute per-day metadata to avoid repeated heavy date ops
    const periodDaysMeta = useMemo(() => {
        return periodDateRange.days.map(day => {
            const iso = day.toISOString().split('T')[0];
            const weekdayShort = day.toLocaleDateString('en-US', { weekday: 'short' });
            const dayNameLower = day.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
            return {
                day,
                iso,
                isHoliday: holidays.has(iso),
                labelShort: formatDateShort(day),
                weekdayShort,
                dayNameLower
            };
        });
    }, [periodDateRange.days, holidays]);

    // Fast lookup for rooms by display name or code
    const roomByNameOrCode = useMemo(() => {
        const map = new Map();
        rooms.forEach(r => {
            const key = r.name || r.room_code;
            if (key) map.set(key, r);
        });
        return map;
    }, [rooms]);

    // Build a map from (dayNameLower|weekStartMs) -> dayIndex for fast lookup
    const dayIndexByNameAndWeekStartMs = useMemo(() => {
        const map = new Map();
        periodDaysMeta.forEach((meta, index) => {
            const weekStartForDay = getWeekStartDate(meta.day).getTime();
            map.set(`${meta.dayNameLower}|${weekStartForDay}`, index);
        });
        return map;
    }, [periodDaysMeta]);

    // Convert schedule entries to grid positions, indexed by room. Stable across zoom.
    const occupancyByRoomId = useMemo(() => {
        const byRoom = new Map(); // Map<roomId, Array<entryInfo>>

        scheduleEntries.forEach(entry => {
            if (!entry || !entry.room) return;

            // Find matching room
            const room = roomByNameOrCode.get(entry.room);
            if (!room) return;

            // Get entry's active weeks for the period
            const entryYear = entry.year || new Date().getFullYear();
            let activeWeeks = [];

            try {
                if (typeof entry.getActiveWeeksSync === 'function') {
                    activeWeeks = entry.getActiveWeeksSync(entryYear, entryYear + 1);
                } else if (entry.weekSeries) {
                    activeWeeks = entry.weekSeries.split(',').map(w => ({
                        week: parseInt(w.trim()),
                        year: entryYear
                    }));
                }
            } catch (e) {
                console.warn('Error getting active weeks for entry:', entry.id, e);
            }

            // Map to days in our period
            activeWeeks.forEach(({ week, year }) => {
                if (!selectedPeriod || !selectedPeriod.containsWeek || !selectedPeriod.containsWeek(week, year)) {
                    return;
                }

                const weekStartMs = getWeekStartDate(new Date(year, 0, 1 + (week - 1) * 7)).getTime();
                const entryDayName = entry.day?.toLowerCase();
                const key = `${entryDayName}|${weekStartMs}`;
                const dayIndex = dayIndexByNameAndWeekStartMs.get(key);
                if (dayIndex === undefined) return;

                // Parse entry time once
                const startTime = entry.timeStart || '09:00';
                const endTime = entry.timeEnd || '10:30';
                const [startHour, startMin] = startTime.split(':').map(Number);
                const [endHour, endMin] = endTime.split(':').map(Number);
                const startMinutes = (startHour - DAY_START_HOUR) * 60 + startMin;
                const endMinutes = (endHour - DAY_START_HOUR) * 60 + endMin;

                if (startMinutes >= 0 && endMinutes <= TOTAL_MINUTES_PER_DAY) {
                    const item = {
                        entry,
                        room,
                        dayIndex,
                        startMinutes,
                        endMinutes
                    };
                    const arr = byRoom.get(room.id) || [];
                    arr.push(item);
                    byRoom.set(room.id, arr);
                }
            });
        });

        return byRoom;
    }, [scheduleEntries, rooms, selectedPeriod, dayIndexByNameAndWeekStartMs]);

    // Render time markers
    const renderTimeMarkers = () => {
        const markers = [];
        const dayWidth = TOTAL_MINUTES_PER_DAY / currentZoom.minutesPerPixel;

        periodDaysMeta.forEach((meta, dayIndex) => {
            const dayStartX = dayIndex * dayWidth;
            const isHoliday = meta.isHoliday;

            // Day header
            markers.push(
                <div
                    key={`day-${dayIndex}`}
                    className={`absolute top-0 border-r border-gray-300 ${isHoliday ? 'bg-red-50' : 'bg-white'}`}
                    style={{
                        left: dayStartX,
                        width: dayWidth,
                        height: TIME_HEADER_HEIGHT / 2
                    }}
                >
                    <div className="p-2 text-sm font-medium text-center">
                        <div>{meta.labelShort}</div>
                        <div className="text-xs text-gray-500">
                            {meta.weekdayShort}
                        </div>
                    </div>
                </div>
            );

            // Scaled time markers within the day (fewer lines at lower zoom)
            const maxMarkersPerZoom = [6, 3, 0, 0, 0];
            const maxMarkers = maxMarkersPerZoom[zoomLevel] || 0;
            if (maxMarkers > 0) {
                const totalHoursSpan = DAY_END_HOUR - DAY_START_HOUR; // 15 hours
                const stepHours = Math.ceil(totalHoursSpan / maxMarkers);
                for (let hour = DAY_START_HOUR; hour <= DAY_END_HOUR; hour += stepHours) {
                    const hourStartMinutes = (hour - DAY_START_HOUR) * 60;
                    const hourX = dayStartX + (hourStartMinutes / currentZoom.minutesPerPixel);

                    markers.push(
                        <div
                            key={`hour-${dayIndex}-${hour}`}
                            className="absolute border-l border-gray-200"
                            style={{
                                left: hourX,
                                top: TIME_HEADER_HEIGHT / 2,
                                height: TIME_HEADER_HEIGHT / 2,
                                width: 1
                            }}
                        >
                            {hour !== DAY_END_HOUR && (
                                <div className="text-xs text-gray-600 ml-1 mt-1">
                                    {hour}:00
                                </div>
                            )}
                        </div>
                    );
                }
            }
        });

        return markers;
    };

    // Render room rows
    const renderRoomRows = () => {
        const items = [];
        for (let i = visibleRange.start; i < visibleRange.end; i++) {
            const room = rooms[i];
            if (!room) continue;

            items.push(
                <div
                    key={room.id}
                    className="border-b border-gray-200"
                    style={{
                        height: ROOM_ROW_HEIGHT,
                        position: 'relative'
                    }}
                >
                    {/* Day backgrounds */}
                    {periodDaysMeta.map((meta, dayIndex) => (
                        <div
                            key={`bg-${dayIndex}`}
                            className={`absolute inset-y-0 border-r border-gray-100 ${meta.isHoliday ? 'bg-red-50' : 'bg-white hover:bg-gray-50'}`}
                            style={{
                                left: dayIndex * dayWidth,
                                width: dayWidth
                            }}
                        />
                    ))}

                    {/* Occupied time slots */}
                    {(occupancyByRoomId.get(room.id) || []).map((data, index) => {
                        const leftPos = (data.dayIndex * dayWidth) + (data.startMinutes / currentZoom.minutesPerPixel);
                        const widthPixels = (data.endMinutes - data.startMinutes) / currentZoom.minutesPerPixel;
                        const subjectHexColor = data.entry?.subjectObj?.color;
                        const colorClass = subjectHexColor ? '' : getColorForCourse(data.entry.course || data.entry.type);
                        const baseBgHex = subjectHexColor || (mapTailwindBgToColor(colorClass) || '#8BEAFE');
                        const gradientStart = baseBgHex;
                        const gradientEnd = data.entry?.type === 'event' ? brightenHexColor(baseBgHex, 0.35) : baseBgHex;
                        const itemKey = `${data.entry.id}-${data.dayIndex}-${index}`;

                        return (
                            <div
                                key={itemKey}
                                className={`absolute text-xs rounded cursor-pointer transition-colors border ${colorClass}`}
                                style={{
                                    left: leftPos,
                                    top: 4,
                                    width: Math.max(widthPixels, 2),
                                    height: ROOM_ROW_HEIGHT - 8,
                                    minWidth: '2px',
                                    ...(data.entry?.type === 'event'
                                        ? { background: `linear-gradient(90deg, ${gradientStart} 0%, ${gradientEnd} 100%)`, borderColor: gradientStart }
                                        : (subjectHexColor ? { backgroundColor: brightenHexColor(subjectHexColor), borderColor: subjectHexColor } : {})
                                    )
                                }}
                                onMouseEnter={() => handleItemMouseEnter(itemKey)}
                                onMouseLeave={handleItemMouseLeave}
                                onClick={() => onTimeSlotClick && onTimeSlotClick(data.entry, data.room, data.dayIndex)}
                            >
                                {/* Event top banner (20% height) - rendered first to stay below text */}
                                {data.entry?.type === 'event' && (
                                    <div
                                        className="absolute pointer-events-none"
                                        style={{
                                            left: 0,
                                            right: 0,
                                            top: 0,
                                            height: '20%',
                                            background: '#FCCAFE',
                                            borderTopLeftRadius: 4,
                                            borderTopRightRadius: 4
                                        }}
                                    />
                                )}
                                {widthPixels > 60 && (
                                    <div className="p-1 overflow-hidden">
                                        <div className="font-medium truncate">
                                            {data.entry.course || 'Class'}
                                        </div>
                                        {widthPixels > 80 && (
                                            <div className="text-xs opacity-90 truncate">
                                                {data.entry.teacher || 'TBD'}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {tooltipState.visible && tooltipState.key === itemKey && (
                                    <div
                                        className="absolute z-50 -top-2 translate-y-full left-1/2 -translate-x-1/2 bg-white text-gray-800 text-xs px-2 py-1 rounded shadow-lg pointer-events-none whitespace-pre"
                                        style={{
                                            whiteSpace: 'pre',
                                            maxWidth: '380px',
                                            width: 'max-content'
                                        }}
                                    >
                                        {typeof data.entry?.getOverviewText === 'function'
                                            ? data.entry.getOverviewText()
                                            : [
                                                (data.entry?.subject || data.entry?.course || data.entry?.title || 'Item'),
                                                (data.entry?.teachersFormatted || data.entry?.teacher || data.entry?.host)
                                                    ? `Teachers: ${data.entry.teachersFormatted || data.entry.teacher || data.entry.host}`
                                                    : null,
                                                (Array.isArray(data.entry?.groups) && data.entry.groups.length > 0)
                                                    ? `Groups: ${data.entry.groups.map(g => (typeof g === 'object' && g) ? (g.name || g.group_code || g.id) : g).filter(Boolean).join(', ')}`
                                                    : null,
                                                (data.entry?.timeStart && data.entry?.timeEnd)
                                                    ? `Time: ${data.entry.timeStart} - ${data.entry.timeEnd}`
                                                    : null
                                            ].filter(Boolean).join('\n')}
                                        <div className="absolute left-1/2 -translate-x-1/2 bottom-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-white" />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            );
        }
        return items;
    };

    if (!selectedPeriod) {
        return (
            <div className="flex items-center justify-center h-64 text-gray-500">
                <div className="text-center">
                    <p>Please select a semester to view room occupancy</p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full bg-white border border-gray-200 rounded-lg overflow-hidden">
            {/* Toolbar */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center space-x-4">
                    <h3 className="text-lg font-medium text-gray-900">
                        Room Occupancy View
                    </h3>
                    <span className="text-sm text-gray-600">
                        {selectedPeriod.name} ({periodDateRange.days.length} days)
                    </span>
                </div>

                <div className="flex items-center space-x-2">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleZoomOut}
                        disabled={zoomLevel >= zoomLevels.length - 1}
                        title="Zoom Out"
                    >
                        <HiMagnifyingGlassMinus className="h-4 w-4" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleZoomIn}
                        disabled={zoomLevel <= 0}
                        title="Zoom In"
                    >
                        <HiMagnifyingGlassPlus className="h-4 w-4" />
                    </Button>
                </div>
            </div>

            {/* Grid Container */}
            <div className="flex-1 flex flex-col overflow-hidden min-h-0 max-h-full">
                {/* Headers Row */}
                <div className="flex border-b border-gray-300">
                    {/* Corner cell */}
                    <div
                        className="bg-gray-100 border-r border-gray-300 flex items-center justify-center"
                        style={{ width: ROOM_HEADER_WIDTH, height: TIME_HEADER_HEIGHT }}
                    >
                        <span className="text-sm font-medium text-gray-700">Rooms</span>
                    </div>

                    {/* Time header - scrollable horizontally */}
                    <div
                        ref={timeHeaderRef}
                        className="flex-1 overflow-hidden bg-gray-50"
                        style={{ height: TIME_HEADER_HEIGHT }}
                    >
                        <div
                            className="relative"
                            style={{ width: totalWidth, height: TIME_HEADER_HEIGHT }}
                        >
                            {renderTimeMarkers()}
                        </div>
                    </div>
                </div>

                {/* Content Row */}
                <div className="flex flex-1 overflow-hidden min-h-0">
                    {/* Room names - fixed width, scrolls with grid */}
                    <div
                        ref={roomHeaderRef}
                        className="bg-gray-50 border-r border-gray-300 overflow-y-auto max-h-full"
                        style={{ width: ROOM_HEADER_WIDTH }}
                        onScroll={handleRoomHeaderScroll}
                    >
                        <div style={{ height: totalHeight, position: 'relative' }}>
                            {/* Top spacer */}
                            <div style={{ height: visibleRange.start * ROOM_ROW_HEIGHT }} />
                            {/* Visible room labels */}
                            {rooms.slice(visibleRange.start, visibleRange.end).map((room) => (
                                <div
                                    key={room.id}
                                    className="border-b border-gray-200 flex items-center px-3 cursor-pointer hover:bg-gray-100"
                                    style={{ height: ROOM_ROW_HEIGHT }}
                                    onClick={() => onRoomClick && onRoomClick(room)}
                                >
                                    <span className="text-sm font-medium text-gray-900 truncate">
                                        {room.name || room.room_code || `Room ${room.id}`}
                                    </span>
                                </div>
                            ))}
                            {/* Bottom spacer */}
                            <div style={{ height: Math.max(0, (rooms.length - visibleRange.end) * ROOM_ROW_HEIGHT) }} />
                        </div>
                    </div>

                    {/* Grid content - scrollable both ways */}
                    <div
                        ref={gridRef}
                        className="flex-1 overflow-auto max-h-full"
                        onScroll={handleGridScroll}
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                        style={{ cursor: 'grab' }}
                    >
                        <div
                            className="relative"
                            style={{ width: totalWidth, height: totalHeight }}
                        >
                            {/* Top spacer for virtualized rows */}
                            <div style={{ height: visibleRange.start * ROOM_ROW_HEIGHT }} />
                            {renderRoomRows()}
                            {/* Bottom spacer */}
                            <div style={{ height: Math.max(0, (rooms.length - visibleRange.end) * ROOM_ROW_HEIGHT) }} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default React.memo(RoomsOccupancyView);
